import { ReactElement, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ProductsAnalyticsProvider } from '@client/analytics/context/products/ProductsAnalyticsContext';
import { SearchAnalyticsProvider } from '@client/analytics/context/search/SearchAnalyticsContext';
import { usePageViewAnalytics, useRemoteRecordsAnalytics } from '@client/analytics/hooks';
import { analyzeProductsListView } from '@client/analytics/redux';
import { EVENT_SOURCE, TEALIUM_PAGE_TYPE } from '@client/analytics/types/types';
import { Page } from '@client/app/components/App/Page';
import { useScrollRestore } from '@client/app/context/ScrollRestoreContext';
import { useBreadcrumbs } from '@client/breadcrumbs/hooks';
import { Catalog } from '@client/catalog/components/Catalog';
import { useSearchData, useSearchQuery } from '@client/catalog/hooks';
import { getCrumbsFromCategories } from '@client/catalog/utils/crumbs';
import { FILTERS } from '@client/catalog/utils/facets';
import { getFilterValueFromSearchParams } from '@client/catalog/utils/filters';
import { isEmpty } from '@client/common/utils/empty/empty';
import { isString } from '@client/common/utils/string/string';
import { useTranslate } from '@client/i18n/hooks';
import { useProductsTiles } from '@client/product/hooks';
import { ROUTES } from '@client/routes/components/Router/routes';
import { useRouterPush, useUrlCreator } from '@client/routes/hooks';
import './CategoryPage.scss';

export const CategoryPage = (): ReactElement => {
  const t = useTranslate();
  const scrollContext = useScrollRestore();
  const { action } = useHistory();
  const push = useRouterPush();
  const urlCreator = useUrlCreator();
  const productTiles = useProductsTiles();
  const homepageUrl = urlCreator(ROUTES.homepage);
  const categoryFilter = getFilterValueFromSearchParams(FILTERS.category);
  const catalogRecord = useSearchData();
  const skus = catalogRecord.data?.products?.map(product => product.sku) || [];
  const filter = isString(categoryFilter)
    ? categoryFilter
    : (categoryFilter as { category: string })?.category;

  const isLowerLevelCategory = filter?.split('/').slice(1).length > 1;
  const query = useSearchQuery();
  const pageType = query
    ? TEALIUM_PAGE_TYPE.productSearch
    : isLowerLevelCategory
      ? TEALIUM_PAGE_TYPE.productSubcategory
      : TEALIUM_PAGE_TYPE.productCategory;

  usePageViewAnalytics({
    remoteRecord: catalogRecord,
    pageType,
    isTriggerBlocked: isEmpty(skus),
  });

  useRemoteRecordsAnalytics(
    [
      catalogRecord,
      ...skus.map(sku => productTiles[sku]),
    ],
    () => analyzeProductsListView({
      eventSource: EVENT_SOURCE.category,
    }),
  );

  useBreadcrumbs(
    catalogRecord.data
      ? getCrumbsFromCategories(
        catalogRecord.data.navigation?.selected,
        urlCreator,
      )
      : [],
  );

  useEffect(() => {
    if (!filter && action !== 'POP') {
      push(homepageUrl);
    }
  }, [filter, action, push]);


  const category = catalogRecord.data?.navigation?.selected?.slice(-1)[0]?.name || '';
  const title = category || '';

  return (
    <ProductsAnalyticsProvider eventSource={EVENT_SOURCE.category}>
      <SearchAnalyticsProvider>
        <Page className='CategoryPage' title={title || t('Category')}>
          <Catalog
            title={title}
            getScrollPosition={scrollContext.getScrollPosition}
          />
        </Page>
      </SearchAnalyticsProvider>
    </ProductsAnalyticsProvider>
  );
};
